/* eslint-disable jsx-a11y/alt-text */
import { useState } from "react"
import { ChevronDown, X, Calendar } from "react-feather"
import {
  Row,
  Col,
  Card,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "reactstrap"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import Map from "./Map"
import { getCamelCaseWord, market } from "../../static/brands_wholesalers_data"
import AnalyticsCard from "./AnalyticsCard"
import usersIcon from "../../assets/users-icon.png"
import boxIcon from "../../assets/box-icon.png"
import graphIcon from "../../assets/graph-icon.png"
import timeIcon from "../../assets/time-icon.png"

// Same format as the brands/retailers table pickers: local start/end of day as ISO, so
// the APIs' `>= start AND < end` includes the whole last day.
const startOfDay = (date) => date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).toISOString() : null
const endOfDay = (date) => date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).toISOString() : null

function Coverage({ data, onlyShow, loading, onLocationSelect, onCardViewClick, location, setLocation, createdDateRange, setCreatedDateRange, updatedDateRange, setUpdatedDateRange, onDateRangeChange }) {
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [createdDatePickerOpen, setCreatedDatePickerOpen] = useState(false)
  const [updatedDatePickerOpen, setUpdatedDatePickerOpen] = useState(false)

  const filters = {}

  if (location) filters.location = [location];

  const filteredLocations = market.filter((m) =>
    m?.label?.toLowerCase().includes(searchTerm.toLowerCase())
  )?.filter(c => c?.label !== 'Open')

  const toggleLocationDropdown = () => setLocationDropdownOpen(!locationDropdownOpen)

  const handleReset = () => {
    setLocation('')
    if (setCreatedDateRange) {
      setCreatedDateRange({ startDate: null, endDate: null })
    }
    if (setUpdatedDateRange) {
      setUpdatedDateRange({ startDate: null, endDate: null })
    }
    if (onLocationSelect) {
      onLocationSelect(null)
    }
  }

  const handleCreatedDateChange = (dates) => {
    const [start, end] = dates
    const newDateRange = {
      startDate: startOfDay(start),
      endDate: endOfDay(end)
    }
    if (setCreatedDateRange) {
      setCreatedDateRange(newDateRange)
    }
    if (end && onDateRangeChange) {
      onDateRangeChange(newDateRange, updatedDateRange)
      setCreatedDatePickerOpen(false)
    }
  }

  const handleUpdatedDateChange = (dates) => {
    const [start, end] = dates
    const newDateRange = {
      startDate: startOfDay(start),
      endDate: endOfDay(end)
    }
    if (setUpdatedDateRange) {
      setUpdatedDateRange(newDateRange)
    }
    if (end && onDateRangeChange) {
      onDateRangeChange(createdDateRange, newDateRange)
      setUpdatedDatePickerOpen(false)
    }
  }

  const formatDateRange = (dateRange, label) => {
    if (!dateRange?.startDate && !dateRange?.endDate) return label
    const start = dateRange?.startDate ? new Date(dateRange.startDate).toLocaleDateString() : ''
    const end = dateRange?.endDate ? new Date(dateRange.endDate).toLocaleDateString() : ''
    if (start && end) return `${start} - ${end}`
    if (start) return `From ${start}`
    return label
  }

  const handleLocationChange = (selectedLocation) => {
    setLocation(selectedLocation)
    setSearchTerm("")
    if (onLocationSelect) {
      onLocationSelect(selectedLocation)
    }
  }

  const handleView = (hoverCountry) => {
    const countryName = hoverCountry.name?.toLowerCase()
    setLocation(countryName)
    if (onLocationSelect) {
      onLocationSelect(countryName)
    }
  };

  const handleCardView = (title) => {
    onCardViewClick(title, location)
  }

  return (
    <Card style={{ flex: 1, padding: '20px', marginTop: '20px' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0 fw-bold">Global Coverage:</h2>
        <div className="d-flex" style={{ gap: 10 }}>
          {(location || createdDateRange?.startDate || updatedDateRange?.startDate) && (
            <button type="button" className="btn" onClick={handleReset}><X size={18} /> Reset</button>
          )}

          <Dropdown isOpen={locationDropdownOpen} toggle={toggleLocationDropdown}>
            <DropdownToggle outline color="secondary" size="md" style={{ color: 'black', borderRadius: '40px', padding: '10px 20px' }}>
              {location ? getCamelCaseWord(location) : 'Location'} <ChevronDown size={18} />
            </DropdownToggle>
            <DropdownMenu>
              <div className="p-2">
                <Input
                  type="text"
                  placeholder="Search Location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: "32px" }}
                  size="sm"
                />
              </div>
              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                {filteredLocations.map((c, index) => (
                  <DropdownItem style={{ color: '#212121', fontWeight: 500 }}
                    key={index}
                    onClick={() => handleLocationChange(c?.label)}
                    active={location === c}
                  >
                    {getCamelCaseWord(c?.label)}
                  </DropdownItem>
                ))}
                {filteredLocations.length === 0 && <DropdownItem style={{ color: '#212121', fontWeight: 500 }} disabled>No location found</DropdownItem>}
              </div>
            </DropdownMenu>
          </Dropdown>

          <Dropdown isOpen={createdDatePickerOpen} toggle={() => setCreatedDatePickerOpen(!createdDatePickerOpen)}>
            <DropdownToggle outline color="secondary" size="md" style={{ color: 'black', borderRadius: '40px', padding: '10px 20px' }}>
              <Calendar size={16} className="me-1" /> {formatDateRange(createdDateRange, 'Created')} <ChevronDown size={18} />
            </DropdownToggle>
            <DropdownMenu style={{ padding: '10px', minWidth: 'auto' }}>
              <DatePicker
                selected={createdDateRange?.startDate ? new Date(createdDateRange.startDate) : null}
                onChange={handleCreatedDateChange}
                startDate={createdDateRange?.startDate ? new Date(createdDateRange.startDate) : null}
                endDate={createdDateRange?.endDate ? new Date(createdDateRange.endDate) : null}
                selectsRange
                inline
                maxDate={new Date()}
              />
            </DropdownMenu>
          </Dropdown>

          <Dropdown isOpen={updatedDatePickerOpen} toggle={() => setUpdatedDatePickerOpen(!updatedDatePickerOpen)}>
            <DropdownToggle outline color="secondary" size="md" style={{ color: 'black', borderRadius: '40px', padding: '10px 20px' }}>
              <Calendar size={16} className="me-1" /> {formatDateRange(updatedDateRange, 'Updated')} <ChevronDown size={18} />
            </DropdownToggle>
            <DropdownMenu style={{ padding: '10px', minWidth: 'auto' }}>
              <DatePicker
                selected={updatedDateRange?.startDate ? new Date(updatedDateRange.startDate) : null}
                onChange={handleUpdatedDateChange}
                startDate={updatedDateRange?.startDate ? new Date(updatedDateRange.startDate) : null}
                endDate={updatedDateRange?.endDate ? new Date(updatedDateRange.endDate) : null}
                selectsRange
                inline
                maxDate={new Date()}
              />
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      <Row>
        <Col lg={6}>
          <Map map={data?.map || {}} onlyShow={onlyShow} onViewBrands={handleView} />
        </Col>

        <Col lg={6}>
          <Row className="g-3 h-100">
            {loading ? (
              <Col className="d-flex justify-content-center align-items-center h-100">
                <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
              </Col>
            ) : data?.cards?.map((card, index) => (
              <Col sm={6} key={index}>
                <AnalyticsCard
                  title={card?.title}
                  count={card?.count}
                  icon={Icons[index]}
                  onViewClick={handleCardView}
                />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Card>
  )
}

const Icons = [usersIcon, boxIcon, graphIcon, timeIcon]

export default Coverage
